import React, { useState } from 'react'
import { ConceptValues, ModuleSizes } from '../../../../../../../types/Survey/Analysis/Concept'
import props from '../../../../../../../redux/props'
import { useSelector } from 'react-redux'
import { formatNumber, round, roundUp } from '../../../../../../../utility'
import { genericQMValues, getFactorWithOriginal } from '../../../../../../hooks/useAnalysisCalculation/getFactorValues'
import {
  PrebuildEinzelarbeitSubType,
  PrebuildMeetingGroesseBesprechungSubType,
  PrebuildMeetingGroesseCoworkingSubType,
  PrebuildType,
} from '../../../../../../../types/Survey/Element'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { BueroType } from '../../../../../../../types/Survey/Analysis/Calculation'
import TableItem, { TableItemRow } from '../../../Summary/TableItem'

type ItemProps = {
  title: string
  value: number
  bold?: boolean
  suffix?: string
  indent?: boolean
  doRound?: boolean
  children?: React.ReactNode
  subItem?: boolean
}

const Item: React.FC<ItemProps> = ({ title, value, bold, suffix = '', indent = false, doRound = true, children, subItem = false }) => {
  const [show, setShow] = useState(false)

  const getNumber = (): string => {
    if (doRound) {
      return `${formatNumber(round(value))}${suffix}`
    }
    return `${formatNumber(value)}${suffix}`
  }

  const toggle = () => {
    if (!!children) {
      setShow(!show)
    }
  }

  const getCaret = () => {
    if (!!children) {
      if (show) {
        return <ChevronUpIcon className="w-4 h-4" />
      }
      return <ChevronDownIcon className="w-4 h-4" />
    }
    return null
  }

  return (
    <div>
      <div className={`flex justify-between ${children && 'cursor-pointer'}`} onClick={toggle}>
        <div
          className={`${!!subItem && 'ml-5'} flex gap-1 items-center w-2/4 ${bold && 'font-bold'} ${children && 'hover:underline'} ${
            indent && 'pl-5'
          }`}
        >
          {getCaret()}
          {title}
        </div>
        <div className={`w-2/4 ${bold && 'font-bold'}`}>{getNumber()}</div>
      </div>
      {show && <div>{children}</div>}
    </div>
  )
}

type ComposerProps = {
  size: ModuleSizes
  qmSize: number
  values: { label: string; amount: number; arbeitsplaetzePerModule: number }[]
  isSquareMeter?: boolean
  titleSuffix?: string
}

const Composer: React.FC<ComposerProps> = ({ size, qmSize, values, titleSuffix }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  const getTotalAmount = () => {
    return values.reduce((acc, n) => (acc += n.amount), 0)
  }

  const getTotalArbeitsplaetze = () => {
    return values.reduce((acc, n) => (acc += n.amount * n.arbeitsplaetzePerModule), 0)
  }

  const getTotalQM = () => {
    return getTotalAmount() * qmSize
  }

  return (
    <TableItem
      title={`${t.survey.analysis.calculation.values.unitSurveyResultsModules.sizes[size]} (${qmSize}${t.generic.squareMeterSuffix}${
        !!titleSuffix ? ` - ${titleSuffix}` : ''
      })`}
      value={getTotalAmount()}
      suffix={`${t.generic.amountSuffix} - ${getTotalArbeitsplaetze()}${
        t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix
      } - ${formatNumber(getTotalQM())}${t.generic.squareMeterSuffix}`}
      doRound
      items={values.map((v) => {
        return (
          <TableItemRow
            key={v.label}
            title={v.label}
            values={[
              { value: v.amount, suffix: t.generic.amountSuffix },
              { value: roundUp(v.amount * v.arbeitsplaetzePerModule), suffix: t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix },
              { value: v.amount * qmSize, suffix: t.generic.squareMeterSuffix },
            ]}
          />
        )
      })}
    />
  )
}

const Modules: React.FC<{ values: ConceptValues }> = ({ values }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  const modulesForSum = [
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT,
      singleAmount: values.factor.arbeitsplatz.EINZELARBEIT.KLASSISCHE_ARBEIT,
      singleQM: values.bueroType === BueroType.ZELLSTRUKTUR ? getFactorWithOriginal(12) : genericQMValues.einzelarbeitQM.KLASSISCHE_ARBEIT,
    },
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
      singleAmount: values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
      singleQM: genericQMValues.einzelarbeitQM.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
    },
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
      singleAmount: values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
      singleQM: genericQMValues.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
      hybridSize: ModuleSizes.XS,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_2,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_2,
      singleQM: genericQMValues.meetingGroesseBesprechungQM.PERSON_2,
      hybridSize: ModuleSizes.XS,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4,
      singleQM: genericQMValues.meetingGroesseBesprechungQM.PERSON_3_4,
      hybridSize: ModuleSizes.S,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8,
      singleQM: genericQMValues.meetingGroesseBesprechungQM.PERSON_5_8,
      hybridSize: ModuleSizes.M,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12,
      singleQM: genericQMValues.meetingGroesseBesprechungQM.PERSON_9_12,
      hybridSize: ModuleSizes.L,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16,
      singleQM: genericQMValues.meetingGroesseBesprechungQM.PERSON_13_16,
      hybridSize: ModuleSizes.XL,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_2_4,
      singleQM: genericQMValues.meetingGroesseCoworkingQM.PERSON_2_4,
      hybridSize: ModuleSizes.S,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_5_8,
      singleQM: genericQMValues.meetingGroesseCoworkingQM.PERSON_5_8,
      hybridSize: ModuleSizes.M,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_9_12,
      singleQM: genericQMValues.meetingGroesseCoworkingQM.PERSON_9_12,
      hybridSize: ModuleSizes.L,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16,
      singleAmount: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_13_16,
      singleQM: genericQMValues.meetingGroesseCoworkingQM.PERSON_13_16,
      hybridSize: ModuleSizes.XL,
    },
  ]

  const getTotalAmount = () => {
    const totalStandard = modulesForSum.reduce((acc, n) => (acc += values.ergebnisse[n.type][n.subType].practicalSpaceAmount), 0)
    const totalHybrid = Object.values(ModuleSizes).reduce((acc, n) => (acc += values.ergebnisse.HYBRID[n].practicalSpaceAmount), 0)
    return totalStandard + totalHybrid
  }

  const getTotalArbeitsplaetze = () => {
    const total = modulesForSum.reduce((acc, n) => {
      let total = values.ergebnisse[n.type][n.subType].practicalSpaceAmount * n.singleAmount.value
      if (n.hybridSize) {
        total += values.ergebnisse.HYBRID[n.hybridSize].practicalSpaceAmount * n.singleAmount.value
      }
      return acc + total
    }, 0)
    return total
  }

  const getTotalQM = () => {
    const total = modulesForSum.reduce((acc, n) => {
      let total = values.ergebnisse[n.type][n.subType].practicalSpaceAmount * n.singleQM.value
      if (n.hybridSize) {
        total += values.ergebnisse.HYBRID[n.hybridSize].practicalSpaceAmount * n.singleQM.value
      }
      return acc + total
    }, 0)
    return total
  }

  return (
    <div className="flex flex-col">
      <Composer
        size={ModuleSizes.XS}
        qmSize={values.bueroType === BueroType.ZELLSTRUKTUR ? 12 : values.factor.einzelarbeitQM.KLASSISCHE_ARBEIT.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsplatz.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.KLASSISCHE_ARBEIT,
            amount: values.ergebnisse.EINZELARBEIT.KLASSISCHE_ARBEIT.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.EINZELARBEIT.KLASSISCHE_ARBEIT.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
            amount: values.ergebnisse.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.value,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.XS}
        qmSize={values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
            amount: values.ergebnisse.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_2,
            amount: values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_2.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_2.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_XS,
            amount: values.ergebnisse.HYBRID.XS.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_2.value,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.S}
        qmSize={values.factor.meetingGroesseBesprechungQM.PERSON_3_4.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_3_4,
            amount: values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_2_4,
            amount: values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_2_4.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_2_4.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_S,
            amount: values.ergebnisse.HYBRID.S.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.value,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.M}
        qmSize={values.factor.meetingGroesseBesprechungQM.PERSON_5_8.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_5_8,
            amount: values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_5_8,
            amount: values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_5_8.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_5_8.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_M,
            amount: values.ergebnisse.HYBRID.M.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.value,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.L}
        qmSize={values.factor.meetingGroesseBesprechungQM.PERSON_9_12.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_9_12,
            amount: values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_9_12,
            amount: values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_9_12.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_9_12.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_L,
            amount: values.ergebnisse.HYBRID.L.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_9_12.value,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.XL}
        qmSize={values.factor.meetingGroesseBesprechungQM.PERSON_13_16.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_13_16,
            amount: values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_13_16,
            amount: values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_13_16.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_13_16.value,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_XL,
            amount: values.ergebnisse.HYBRID.XL.practicalSpaceAmount,
            arbeitsplaetzePerModule: values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_13_16.value,
          },
        ]}
      />
      <Item
        bold
        title={t.survey.analysis.concept.summary.areaOverview.total}
        value={getTotalAmount()}
        suffix={`${t.generic.amountSuffix} - ${getTotalArbeitsplaetze()}${
          t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix
        } - ${formatNumber(getTotalQM())}${t.generic.squareMeterSuffix}`}
      />
    </div>
  )
}

export default Modules
